import React, { useEffect, useState, useCallback } from "react";
import {
  Loader2, TrendingUp, TrendingDown, Wallet, Users, FileText, CalendarClock,
  ClipboardCheck, AlertTriangle, MapPin, ArrowRight, ListTodo, Flag, Sparkles, Clock,
} from "lucide-react";
import { api, adminHeaders } from "@/lib/api";

const h = () => ({ headers: adminHeaders() });
const shekel = (n) => `\u20aa${Number(n || 0).toLocaleString()}`;
const today = () => new Date().toISOString().slice(0, 10);

const greeting = () => {
  const hr = new Date().getHours();
  if (hr < 12) return "Good morning";
  if (hr < 18) return "Good afternoon";
  return "Good evening";
};

const StatCard = ({ icon: Icon, label, value, sub, tone = "gold", onClick, testid }) => {
  const tones = {
    gold: "text-gold-soft", emerald: "text-emerald-300", red: "text-red-300", white: "text-white",
  };
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`ascendia-card p-5 text-left ${onClick ? "hover:border-[color:var(--border-strong)] transition-colors cursor-pointer" : "cursor-default"}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-c">{label}</span>
        <Icon className={`h-4 w-4 ${tones[tone]}`} />
      </div>
      <p className={`mt-2 font-display text-2xl font-semibold ${tones[tone]}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-c">{sub}</p>}
    </button>
  );
};

const JobRow = ({ j, onOpen }) => (
  <button onClick={onOpen} className="w-full text-left rounded-xl border border-[color:var(--border-subtle)] px-3.5 py-2.5 hover:bg-white/5 transition-colors" data-testid={`cc-job-${j.id}`}>
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm text-white truncate">{j.client_name || "Client"} · <span className="text-muted-c">{j.service || "Service"}</span></p>
        <p className="text-xs text-muted-c truncate flex items-center gap-1"><MapPin className="h-3 w-3" /> {j.city || j.address || "—"}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-xs text-gold-soft">{j.appointment_time || j.appointment_date || ""}</p>
        <p className="text-[11px] text-muted-c">{j.status}</p>
      </div>
    </div>
  </button>
);

export default function CommandCenter({ onNav }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/command-center", h()).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-16 flex justify-center text-muted-c"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return null;

  const { money, sales, tasks, today_jobs = [], upcoming_jobs = [], needs_attention = {}, counts = {} } = data;
  const attention = [
    { key: "needs_inspection", label: "Needs inspection", icon: ClipboardCheck, list: needs_attention.needs_inspection || [], tab: "jobs" },
    { key: "unpaid_deposit", label: "Deposit unpaid", icon: Wallet, list: needs_attention.unpaid_deposit || [], tab: "jobs" },
    { key: "unscheduled", label: "Not scheduled", icon: CalendarClock, list: needs_attention.unscheduled || [], tab: "scheduling" },
  ].filter((a) => a.list.length > 0);

  return (
    <div className="space-y-6" data-testid="command-center">
      <div>
        <p className="kicker mb-1">Command Center</p>
        <h2 className="font-display text-2xl font-semibold text-white">{greeting()} — here's your day</h2>
        <p className="mt-1 text-sm text-muted-c">{new Date(data.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
      </div>

      {/* Money today (owner only) */}
      {money && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="cc-money">
          <StatCard icon={TrendingUp} tone="emerald" label="Collected today" value={shekel(money.collected_today)} testid="cc-collected" />
          <StatCard icon={TrendingDown} tone="red" label="Spent today" value={shekel(money.spent_today)} testid="cc-spent" />
          <StatCard icon={Wallet} tone={money.net_today >= 0 ? "gold" : "red"} label="Net today" value={shekel(money.net_today)} testid="cc-net" onClick={() => onNav && onNav("finance")} sub="View finance" />
        </div>
      )}

      {/* Sales snapshot */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard icon={Users} label="New leads" value={sales.new_leads} tone="gold" onClick={() => onNav && onNav("leads")} sub="Chase these" testid="cc-new-leads" />
        <StatCard icon={FileText} label="Quotes awaiting" value={sales.quotes_awaiting} tone="gold" onClick={() => onNav && onNav("quotes")} sub="Follow up" testid="cc-quotes-awaiting" />
        <StatCard icon={CalendarClock} label="Booking requests" value={sales.pending_bookings} tone="gold" onClick={() => onNav && onNav("scheduling")} sub="Confirm dates" testid="cc-pending-bookings" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today + upcoming */}
        <div className="ascendia-card p-5" data-testid="cc-schedule">
          <div className="flex items-center gap-2 mb-3"><CalendarClock className="h-4 w-4 text-gold-soft" /><h3 className="font-display font-semibold text-white">Today's schedule</h3><span className="ml-auto text-xs text-muted-c">{counts.today || 0} today</span></div>
          {today_jobs.length === 0 ? (
            <p className="text-sm text-muted-c py-3">No jobs scheduled for today.</p>
          ) : (
            <div className="space-y-2">{today_jobs.map((j) => <JobRow key={j.id} j={j} onOpen={() => onNav && onNav("jobs")} />)}</div>
          )}
          {upcoming_jobs.length > 0 && (
            <>
              <p className="mt-4 mb-2 text-xs uppercase tracking-wide text-muted-c">Coming up</p>
              <div className="space-y-2">{upcoming_jobs.map((j) => <JobRow key={j.id} j={j} onOpen={() => onNav && onNav("jobs")} />)}</div>
            </>
          )}
        </div>

        {/* Needs attention */}
        <div className="ascendia-card p-5" data-testid="cc-attention">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-4 w-4 text-gold-soft" /><h3 className="font-display font-semibold text-white">Needs attention</h3></div>
          {attention.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-c flex flex-col items-center gap-2"><Sparkles className="h-5 w-5 text-emerald-300" /> All clear — nothing needs chasing.</div>
          ) : (
            <div className="space-y-4">
              {attention.map(({ key, label, icon: Icon, list, tab }) => (
                <div key={key} data-testid={`cc-attention-${key}`}>
                  <button onClick={() => onNav && onNav(tab)} className="w-full flex items-center gap-2 text-sm text-white/90 hover:text-white">
                    <Icon className="h-4 w-4 text-gold-soft" /> {label}
                    <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-c">{list.length} <ArrowRight className="h-3 w-3" /></span>
                  </button>
                  <div className="mt-2 space-y-1.5 pl-6">
                    {list.slice(0, 3).map((j) => (
                      <p key={j.id} className="text-xs text-muted-c truncate">{j.client_name || "Client"} · {j.service || "Service"}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Open tasks */}
      <div className="ascendia-card p-5" data-testid="cc-tasks">
        <div className="flex items-center gap-2 mb-3">
          <ListTodo className="h-4 w-4 text-gold-soft" /><h3 className="font-display font-semibold text-white">Open tasks</h3>
          <span className="ml-auto flex items-center gap-2 text-xs text-muted-c">
            {tasks.overdue > 0 && <span className="text-red-300">{tasks.overdue} overdue</span>}
            <button onClick={() => onNav && onNav("tasks")} className="inline-flex items-center gap-1 text-gold-soft hover:text-gold">All tasks <ArrowRight className="h-3 w-3" /></button>
          </span>
        </div>
        {(tasks.items || []).length === 0 ? (
          <p className="text-sm text-muted-c py-2">No open tasks. Add one from Quick add.</p>
        ) : (
          <div className="space-y-2">
            {tasks.items.map((t) => {
              const overdue = t.due_date && t.due_date < today();
              return (
                <div key={t.id} className="flex items-center gap-3 rounded-xl border border-[color:var(--border-subtle)] px-3.5 py-2.5" data-testid={`cc-task-${t.id}`}>
                  <Flag className={`h-3.5 w-3.5 flex-shrink-0 ${t.priority === "high" ? "text-red-300" : t.priority === "low" ? "text-white/40" : "text-gold-soft"}`} />
                  <p className="flex-1 text-sm text-white truncate">{t.title}</p>
                  {t.due_date && <span className={`text-xs flex items-center gap-1 ${overdue ? "text-red-300" : "text-muted-c"}`}><Clock className="h-3 w-3" /> {t.due_date}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
